import React, { useState } from 'react';
import { useSuspension } from '../contexts/SuspensionContext';
import { AlertTriangle, X, Mail, Lock } from 'lucide-react';

const SuspensionBanner = () => {
  const { isSuspended, suspensionReason, isLoading } = useSuspension();
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if not suspended, loading, or dismissed
  if (!isSuspended || isLoading || isDismissed) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        backgroundColor: '#fef2f2',
        border: '2px solid #ef4444',
        borderRadius: '12px',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 4px 20px rgba(239, 68, 68, 0.25)',
        maxWidth: '90%',
        width: 'auto',
      }}
    >
      <div
        style={{
          backgroundColor: '#ef4444',
          borderRadius: '50%',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Lock size={18} color="#ffffff" />
      </div>

      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: '700',
          color: '#991b1b',
          fontSize: '14px',
          marginBottom: '2px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <AlertTriangle size={16} />
          Organization Suspended - Read-Only Mode
        </div>
        <div style={{ color: '#b91c1c', fontSize: '12px' }}>
          You can view all data but actions are disabled.
          {suspensionReason && (
            <span style={{ marginLeft: '6px', fontStyle: 'italic' }}>
              Reason: {suspensionReason}
            </span>
          )}
        </div>
      </div>

      <a
        href="mailto:support@arthatrack.com"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          color: '#ffffff',
          textDecoration: 'none',
          fontSize: '12px',
          fontWeight: '600',
          padding: '6px 12px',
          backgroundColor: '#ef4444',
          borderRadius: '6px',
          transition: 'background-color 0.2s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = '#dc2626')}
        onMouseLeave={(e) => (e.target.style.backgroundColor = '#ef4444')}
      >
        <Mail size={12} />
        Contact Support
      </a>

      <button
        onClick={() => setIsDismissed(true)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          color: '#991b1b',
          marginLeft: '4px',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = '#fecaca')}
        onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
        title="Dismiss"
      >
        <X size={18} />
      </button>
    </div>
  );
};

// Helper component to wrap buttons and disable them when org is suspended
export const SuspendedButton = ({ children, onClick, disabled, style, ...props }) => {
  const { isSuspended } = useSuspension();

  const isDisabled = disabled || isSuspended;

  return (
    <button
      {...props}
      onClick={isDisabled ? (e) => e.preventDefault() : onClick}
      disabled={isDisabled}
      style={{
        ...style,
        ...(isSuspended ? {
          opacity: 0.5,
          cursor: 'not-allowed',
          pointerEvents: 'none',
        } : {}),
      }}
      title={isSuspended ? 'Actions are disabled - organization is suspended' : props.title}
    >
      {children}
    </button>
  );
};

// Helper component to show a "disabled" overlay on action areas
export const SuspendedOverlay = ({ children }) => {
  const { isSuspended } = useSuspension();

  if (!isSuspended) {
    return children;
  }

  return (
    <div style={{ position: 'relative' }}>
      {children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          cursor: 'not-allowed',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#991b1b',
            fontSize: '13px',
            fontWeight: '1000',
          }}
        >
          <Lock size={16} />
          Actions disabled - Organization suspended
        </div>
      </div>
    </div>
  );
};

export default SuspensionBanner;
